import {baseUri} from '@jetbrains/teamcity-ui'
import type {KeyValue} from '@jetbrains/teamcity-ui/dist/utils'

import type {ProjectsTree, TreeFilter} from '../selectors/projectTrees'
import type {BuildType, Pipeline} from '../services/rest'
import type {
  BuildTypeId,
  FederationServerId,
  NodeGroup,
  ProjectId,
  Template,
  ExpandState,
  SidebarProject,
} from '../types'
import {toBuildTypeId, ROOT_PROJECT_ID} from '../types'
import type {ProjectsTreeItem} from '../types/projectTrees'

import {getSettingsAvailable} from './entityLinks'
import type {WritableMap} from './object'

const currentServerId: FederationServerId = baseUri

type ProcessProjectProps = {
  project: SidebarProject
  parentId?: ProjectId
  childrenByProject: WritableMap<ProjectId, ReadonlyArray<SidebarProject>>
  level: number
  treeData: Array<ProjectsTreeItem>
  group: NodeGroup
  serverId?: FederationServerId
  removePipelines: boolean
  removeBuildTypes: boolean
  disabledProjects?: ReadonlyArray<ProjectId> | null | undefined
  isIncluded?: boolean
}

type ProcessChildrenProps = {
  child: SidebarProject
  childId: ProjectId
  parentId: ProjectId
  childrenByProject: WritableMap<ProjectId, ReadonlyArray<SidebarProject>>
  level: number
  treeData: Array<ProjectsTreeItem>
  group: NodeGroup
  serverId?: FederationServerId
  removePipelines: boolean
  removeBuildTypes: boolean
  includedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
  excludedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
  disabledProjects?: ReadonlyArray<ProjectId> | null | undefined
  includedParents: Set<ProjectId>
  showArchivedProjects?: boolean
  isIncluded?: boolean
  removeSiblingBuildTypes?: boolean
}

type ProcessPipelinesProps = {
  project: SidebarProject | null | undefined
  level: number
  treeData: ProjectsTreeItem[]
  group: NodeGroup
  serverId?: FederationServerId
}

type ProcessBuildTypesProps = {
  project: SidebarProject | null | undefined
  level: number
  treeData: ProjectsTreeItem[]
  group: NodeGroup
  serverId?: FederationServerId
  includedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
  excludedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
}

type ConvertDataToTreeProps = {
  ready: boolean
  data: ReadonlyArray<ProjectId>
  projects: KeyValue<ProjectId, SidebarProject>
  group: NodeGroup
  templates?: KeyValue<ProjectId, readonly Template[]>
  allProjects?: KeyValue<ProjectId, SidebarProject>
  serverId?: FederationServerId
  filter?: TreeFilter
  onlyIncluded?: boolean
  showArchivedProjects?: boolean
}

type GetValidParentsProps = {
  data: ReadonlyArray<ProjectId>
  projects: KeyValue<ProjectId, SidebarProject>
  onlyIncluded?: boolean
}

type GetChildrenByProjectProps = {
  data: ReadonlyArray<ProjectId>
  validParents: WritableMap<ProjectId, SidebarProject | undefined>
  projects: KeyValue<ProjectId, SidebarProject>
}

export function getValidParents({data, projects, onlyIncluded}: GetValidParentsProps) {
  const validParents: WritableMap<ProjectId, SidebarProject | undefined> = new Map()
  const hashMap = new Map<ProjectId, boolean>(data.map(projectId => [projectId, true]))
  const addedParents = new Set<ProjectId>()

  data.forEach((projectId: ProjectId) => {
    const currentProject = projects[projectId]
    let currentId = projectId
    let parentId = currentProject?.parentProjectId

    if (currentProject != null) {
      const ancestorProjects = currentProject.ancestorProjects?.project

      if (onlyIncluded) {
        parentId = ancestorProjects?.findLast(id => hashMap.has(id))
      }

      validParents.set(currentId, projects[parentId ?? ROOT_PROJECT_ID])

      if (!onlyIncluded) {
        while (
          parentId &&
          parentId !== ROOT_PROJECT_ID &&
          (!hashMap.has(currentId) || !hashMap.has(parentId))
        ) {
          const project = projects[parentId]
          if (project != null) {
            if (currentId !== projectId) {
              validParents.set(currentId, project)
            }
            if (!hashMap.has(parentId)) {
              addedParents.add(parentId)
            }
          }
          currentId = parentId
          parentId = project?.parentProjectId
        }
      }
    }
  })

  return {validParents, addedParents: [...addedParents]}
}

export function getChildrenByProject({data, validParents, projects}: GetChildrenByProjectProps) {
  const childrenByProject: WritableMap<ProjectId, Array<SidebarProject>> = new Map()

  data.forEach((projectId: ProjectId) => {
    const project = projects[projectId]

    if (project) {
      if (!childrenByProject.has(projectId)) {
        childrenByProject.set(projectId, [])
      }

      if (projectId === ROOT_PROJECT_ID) {
        return
      }

      const validParent = validParents.get(project.id)
      const parentProjectId = validParent ? validParent.id : ROOT_PROJECT_ID
      const siblings = childrenByProject.get(parentProjectId)

      if (siblings) {
        siblings.push(project)
      } else {
        childrenByProject.set(parentProjectId, [project])
      }
    }
  })

  return childrenByProject
}

function processProject({
  project,
  parentId,
  childrenByProject,
  level,
  treeData,
  group,
  serverId = currentServerId,
  removePipelines,
  removeBuildTypes,
  disabledProjects,
  isIncluded,
}: ProcessProjectProps) {
  const projectId = project.id ?? ''
  const pipelines = removePipelines ? [] : (project.pipelines?.pipeline ?? [])
  const buildTypes = removeBuildTypes ? [] : (project.buildTypes?.buildType ?? [])
  const children = childrenByProject.get(projectId) ?? []
  const pipelinesLength = project.pipelines?.pipeline?.length ?? 0
  const hasChildren = buildTypes.length > 0 || children.length > 0 || pipelinesLength > 0
  const itemType = 'project'
  const key = `${serverId}_${itemType}_${projectId}`

  treeData.push({
    itemType,
    id: projectId,
    internalId: project.internalId,
    name: project.name || '',
    level,
    parentId,
    actualParentId: project.parentProjectId,
    hasChildren,
    archived: project.archived,
    childrenProjectsIds: children.map(child => child.id),
    childrenPipelinesIds: pipelines.map(pipeline => pipeline.id!),
    childrenBuildTypesIds: buildTypes.map(buildType => buildType.id!),
    searchMatches: null,
    expanded: true,
    group,
    serverId,
    focusable: true,
    isDisabled: !isIncluded || disabledProjects?.includes(projectId),
    key,
    noSettingsAccess: !getSettingsAvailable(project),
    isReadOnly: project.readOnlyUI?.value === true,
  })
}

function processChildren({
  child,
  childId,
  parentId,
  childrenByProject,
  level,
  treeData,
  group,
  serverId = currentServerId,
  removePipelines,
  removeBuildTypes,
  includedBuildTypes,
  excludedBuildTypes,
  disabledProjects,
  includedParents,
  showArchivedProjects,
  isIncluded,
  removeSiblingBuildTypes,
}: ProcessChildrenProps) {
  if (!isIncluded && !includedParents.has(childId)) {
    return
  }

  processProject({
    project: child,
    parentId,
    childrenByProject,
    level,
    treeData,
    group,
    serverId,
    removePipelines,
    removeBuildTypes,
    disabledProjects,
    isIncluded: isIncluded || removeSiblingBuildTypes,
  })

  if (!removeBuildTypes && isIncluded && (!child.archived || showArchivedProjects)) {
    processBuildTypes({
      project: child,
      level: level + 1,
      treeData,
      group,
      serverId,
      includedBuildTypes,
      excludedBuildTypes,
    })
  }

  if (includedBuildTypes == null) {
    processPipeline({
      project: child,
      level: level + 1,
      treeData,
      group,
      serverId,
    })
  }
}

function processPipeline({
  project,
  level,
  treeData,
  group,
  serverId = currentServerId,
}: ProcessPipelinesProps) {
  if (project == null) {
    return
  }

  const list: ReadonlyArray<Pipeline> = project.pipelines?.pipeline ?? []

  list.forEach(({id, internalId, name}: Pipeline) => {
    const itemType = 'pipeline'
    const key = `${serverId}_${itemType}_${id}`
    const item: ProjectsTreeItem = {
      itemType,
      internalId,
      id,
      name,
      level,
      parentId: project.id,
      archived: project.archived,
      type: null,
      searchMatches: null,
      group,
      serverId,
      focusable: true,
      key,
    }

    treeData.push(item)
  })
}

function processBuildTypes({
  project,
  level,
  treeData,
  group,
  serverId = currentServerId,
  includedBuildTypes,
  excludedBuildTypes,
}: ProcessBuildTypesProps) {
  if (project == null) {
    return
  }

  let types: ReadonlyArray<BuildType> = project.buildTypes?.buildType ?? []

  if (includedBuildTypes != null) {
    types = types.filter(buildType => includedBuildTypes.includes(buildType.id!))
  }

  if (excludedBuildTypes != null) {
    types = types.filter(buildType => !excludedBuildTypes.includes(buildType.id!))
  }

  types.forEach((buildType: BuildType) => {
    const {id = toBuildTypeId(''), name = '', type, internalId, paused} = buildType
    const itemType = 'buildType'
    const key = `${serverId}_${itemType}_${id}`
    const item: ProjectsTreeItem = {
      itemType,
      internalId,
      id,
      name,
      level,
      parentId: project.id,
      parentInternalId: project.internalId,
      archived: project.archived,
      type,
      searchMatches: null,
      group,
      serverId,
      focusable: true,
      key,
      noSettingsAccess: !getSettingsAvailable(buildType),
      paused,
    }

    treeData.push(item)
  })
}

/**
 * Info: Performance improvement
 * This method is used in different places with big data. Please be careful to editing it
 */
export function convertDataToTree({
  ready,
  data,
  projects,
  group,
  serverId,
  filter = {},
  onlyIncluded,
  showArchivedProjects,
}: ConvertDataToTreeProps): ProjectsTree | null | undefined {
  if (!ready) {
    return null
  }

  const treeData: Array<ProjectsTreeItem> = []
  const {
    parentId = ROOT_PROJECT_ID,
    removePipelines = false,
    removeBuildTypes = false,
    removeEmptyProjects = false,
    removeSiblingBuildTypes = false,
    includedBuildTypes,
    excludedBuildTypes,
    includedProjects,
    includedRootProjects,
    disabledProjects,
    includeRoot = false,
  } = filter
  const includedProjectsSet = new Set(includedProjects)
  const includedRootProjectsSet = new Set(includedRootProjects)
  const getIsIncludedProjects = (id: ProjectId) =>
    includedProjects == null || includedProjectsSet.has(id)
  const getIsIncludedRootProjects = (id: ProjectId) =>
    includedRootProjects == null || includedRootProjectsSet.has(id)
  const baseLevel = group === 'all' || includeRoot ? 1 : 0
  let filteredData = data

  if (removeEmptyProjects || includedBuildTypes != null) {
    const nonEmpty = new Set()

    const markAsNonEmptyRecursively = (id: ProjectId | null | undefined) => {
      if (id != null && !nonEmpty.has(id)) {
        nonEmpty.add(id)
        markAsNonEmptyRecursively(projects[id]?.parentProjectId)
      }
    }

    data.forEach(projectId => {
      const project = projects[projectId]
      const childBuildTypes = project?.buildTypes?.buildType ?? []
      const childPipelines = project?.pipelines?.pipeline ?? []

      if (
        childPipelines.length > 0 ||
        (childBuildTypes.length > 0 &&
          (includedBuildTypes == null ||
            childBuildTypes.some(buildType => includedBuildTypes.includes(buildType.id!))) &&
          (excludedBuildTypes == null ||
            childBuildTypes.some(buildType => !excludedBuildTypes.includes(buildType.id!))))
      ) {
        markAsNonEmptyRecursively(project?.id)
      }
    })
    filteredData = data.filter(id => nonEmpty.has(id))
  }

  const includedParents = new Set<ProjectId>()

  const includeParentsRecursively = (id: ProjectId) => {
    const parentProjectId = projects[id]?.parentProjectId

    if (parentProjectId != null && !includedParents.has(parentProjectId)) {
      includedParents.add(parentProjectId)
      includeParentsRecursively(parentProjectId)
    }
  }

  includedProjects?.forEach(includeParentsRecursively)
  includedRootProjects?.forEach(includeParentsRecursively)

  const {validParents, addedParents} = getValidParents({
    data: filteredData,
    projects,
    onlyIncluded,
  })

  const childrenByProject = getChildrenByProject({
    data: filteredData.concat(addedParents),
    validParents,
    projects,
  })

  const parent = projects[parentId]

  if (includeRoot && parent != null) {
    const isIncluded = getIsIncludedProjects(parent.id) && getIsIncludedRootProjects(parent.id)

    processProject({
      project: parent,
      childrenByProject,
      level: 0,
      treeData,
      group,
      serverId,
      removePipelines,
      removeBuildTypes,
      disabledProjects,
      isIncluded,
    })
  }

  if (!removeBuildTypes && getIsIncludedProjects(parentId)) {
    processBuildTypes({
      project: projects[parentId],
      level: baseLevel,
      treeData,
      group,
      serverId,
      includedBuildTypes,
      excludedBuildTypes,
    })
  }

  /**
   * Info: The Pipeline needs to show only the specific project
   */
  if (parentId !== ROOT_PROJECT_ID && includedBuildTypes == null) {
    processPipeline({
      project: projects[parentId],
      level: baseLevel,
      treeData,
      group,
      serverId,
    })
  }

  const rootItems = childrenByProject.get(parentId)
  const rootLength = rootItems?.length

  if (rootLength) {
    const parentIds = [parentId, parentId]
    let level = baseLevel
    let includedLevel = level
    let stack: Array<SidebarProject | null> = [...rootItems]
    const processChildrenArgumens: ProcessChildrenProps = {
      child: parent!,
      parentId,
      childId: parentId,
      childrenByProject,
      treeData,
      group,
      serverId,
      level,
      removePipelines,
      removeBuildTypes,
      includedBuildTypes,
      excludedBuildTypes,
      disabledProjects,
      includedParents,
      showArchivedProjects,
    }

    while (stack.length) {
      const parentChild = stack.shift()

      if (parentChild != null) {
        const {id} = parentChild
        const currentParentId = parentIds[level]
        const parentChilds: Array<SidebarProject> | undefined = childrenByProject.get(id)
        const hasChild = parentChilds?.length
        const currentLevelHigher = level <= includedLevel
        const isIncludedProject = getIsIncludedProjects(id)
        const isIncludedRootProject = currentLevelHigher ? getIsIncludedRootProjects(id) : true
        const isIncluded = isIncludedProject && isIncludedRootProject

        processChildrenArgumens.child = parentChild
        processChildrenArgumens.childId = id
        processChildrenArgumens.level = level
        processChildrenArgumens.parentId = currentParentId
        processChildrenArgumens.isIncluded = isIncluded
        processChildrenArgumens.removeSiblingBuildTypes = removeSiblingBuildTypes

        processChildren(processChildrenArgumens)

        if (hasChild) {
          level++
          parentIds[level] = id
          stack = [...parentChilds, null, ...stack]
        }

        if (currentLevelHigher && !isIncluded) {
          includedLevel = level
        }
      } else {
        level--
        if (level < includedLevel) {
          includedLevel = level
        }
      }
    }
  }

  return {
    data: treeData,
    _hash: undefined,
    /**
     * Info: Performance improvement
     * Creating a deferred hashmap on demand by the object getter
     */
    get hash() {
      if (!this._hash) {
        this._hash = new Map(
          this.data.map((item, index) => {
            item.index = index
            return [item.key!, item]
          }),
        )
      }

      return this._hash
    },
  }
}

export const upgradeTreeByExpandState = ({
  defaultTree,
  expandState,
  baseLevel,
  shift,
  showArchivedProjects,
}: {
  defaultTree: ProjectsTree | null | undefined
  expandState: KeyValue<ProjectId, ExpandState> | null | undefined
  baseLevel: number
  shift?: number
  showArchivedProjects?: boolean
}): ProjectsTree => {
  const treeData: Array<ProjectsTreeItem> = []

  if (defaultTree?.data) {
    const localExpandState: WritableMap<ProjectId, boolean> = new Map()
    defaultTree.data.forEach((item: ProjectsTreeItem) => {
      const {parentId, itemType, id} = item
      const newItem = {...item}

      if (item.level != null && shift != null) {
        newItem.level += shift
      }

      if (itemType === 'project') {
        if (parentId && localExpandState.get(parentId) === false) {
          localExpandState.set(item.id, false)
          newItem.expanded = false
        } else {
          const defaultExpanded = !showArchivedProjects && item.archived ? 'EXPANDED' : 'COLLAPSED'
          const expandStateWithDefault = expandState?.[id]?.expandState ?? defaultExpanded
          const expanded = expandStateWithDefault === 'EXPANDED'
          localExpandState.set(id, expanded)
          newItem.expanded = expanded
        }
      }

      if (newItem.level === baseLevel || (parentId && localExpandState.get(parentId))) {
        treeData.push(newItem)
      }
    })
  }

  return {
    data: treeData,
    _hash: undefined,
    /**
     * Info: Performance improvement
     * Creating a deferred hashmap on demand by the object getter
     */
    get hash() {
      if (!this._hash) {
        this._hash = new Map(
          treeData.map(item => [`${item.serverId}_${item.itemType}_${item.id}`, item]),
        )
      }
      return this._hash
    },
  }
}
